import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Check, Star, X, ZoomIn, ChevronLeft, ChevronRight as ChevronRightIcon, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Step2KitSelection = ({ 
  products, gardenSize, recommendedKit, upsellKit, selectedKit, onSubmit, onBack, isLoading
}) => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(selectedKit);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const scrollRef = useRef(null);

  const sortedProducts = [...products].sort((a, b) => a.coverage - b.coverage);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (recommendedKit) {
      const index = sortedProducts.findIndex(p => p.id === recommendedKit.id);
      if (index !== -1) {
        const newIdx = isMobile ? index : Math.max(0, index - 1);
        setCurrentIndex(newIdx);
        // Delayed scroll for mobile to ensure DOM is ready
        if (isMobile) {
          setTimeout(() => {
            if (scrollRef.current && scrollRef.current.children[index]) {
              const card = scrollRef.current.children[index];
              scrollRef.current.scrollTo({ left: card.offsetLeft - 16, behavior: 'smooth' });
            }
          }, 300);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedKit, isMobile]);

  useEffect(() => {
    if (!selected && recommendedKit) setSelected(recommendedKit);
  }, [recommendedKit, selected]);

  // Programmatic scroll on mobile when arrows are used
  const scrollToIndex = useCallback((idx) => {
    if (scrollRef.current && scrollRef.current.children[idx]) {
      const card = scrollRef.current.children[idx];
      scrollRef.current.scrollTo({ left: card.offsetLeft - 16, behavior: 'smooth' });
    }
  }, []);

  const isRecommended = (product) => recommendedKit && product.id === recommendedKit.id;
  const isUpsell = (product) => upsellKit && product.id === upsellKit.id;
  const handleSelect = (product) => setSelected(product);
  const handleSubmit = () => { if (selected) onSubmit(selected); };
  const openLightbox = (image, e) => { e.stopPropagation(); setLightboxImage(image); };

  const desktopVisible = 3;
  const maxDesktopIndex = Math.max(0, sortedProducts.length - desktopVisible);

  const slideLeft = () => {
    const newIdx = Math.max(0, currentIndex - 1);
    setCurrentIndex(newIdx);
    if (isMobile) scrollToIndex(newIdx);
  };
  const slideRight = () => {
    const max = isMobile ? sortedProducts.length - 1 : maxDesktopIndex;
    const newIdx = Math.min(max, currentIndex + 1);
    setCurrentIndex(newIdx);
    if (isMobile) scrollToIndex(newIdx);
  };

  // Handle mobile scroll snap
  const handleScroll = useCallback(() => {
    if (!isMobile || !scrollRef.current) return;
    const container = scrollRef.current;
    const cardWidth = container.children[0]?.offsetWidth || 300;
    const newIndex = Math.round(container.scrollLeft / (cardWidth + 12));
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < sortedProducts.length) {
      setCurrentIndex(newIndex);
    }
  }, [isMobile, currentIndex, sortedProducts.length]);

  const getCoverageBadge = (product) => {
    if (!gardenSize || gardenSize <= 0) return null;
    const coverage = product.coverage;
    const threshold = gardenSize * 1.3;
    if (coverage >= gardenSize && coverage <= threshold) return { type: 'perfect', text: `Perfetto per ~${gardenSize.toLocaleString()} m²` };
    if (coverage > threshold) return { type: 'reserve', text: `+${(coverage - gardenSize).toLocaleString()} m² riserva` };
    return { type: 'warning', text: `Solo ~${coverage.toLocaleString()} m²` };
  };

  const ProductCard = ({ product, index }) => {
    const isSelected = selected?.id === product.id;
    const recommended = isRecommended(product);
    const upsell = isUpsell(product);
    const badge = getCoverageBadge(product);

    return (
      <div
        data-testid={`kit-card-${product.id}`}
        onClick={() => handleSelect(product)}
        className={`relative border-2 cursor-pointer transition-all rounded-xl bg-white flex-shrink-0
          ${isMobile ? 'w-[85vw] snap-center' : 'w-[calc(33.333%-0.75rem)]'}
          ${isSelected ? 'border-stocker-green shadow-xl ring-4 ring-stocker-green/30' : recommended ? 'border-stocker-green/40 shadow-lg' : upsell ? 'border-stocker-orange/40 shadow-lg' : 'border-gray-200 shadow-md hover:border-gray-300'}`}
      >
        {/* Badges */}
        {recommended && (
          <div data-testid={`recommended-badge-${product.id}`} className="absolute -top-3 left-3 z-10 bg-stocker-green text-white text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 uppercase shadow-sm">
            <Check className="w-3 h-3" /> Consigliato
          </div>
        )}
        {upsell && !recommended && (
          <div className="absolute -top-3 left-3 z-10 bg-stocker-orange text-white text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 uppercase shadow-sm">
            <Star className="w-3 h-3" /> {t.morePower}
          </div>
        )}

        <div className="p-4 md:p-3">
          {/* Image — prominent on both mobile and desktop */}
          <div className="relative bg-gray-50 rounded-lg overflow-hidden group mb-3" style={{ aspectRatio: '4/3' }}>
            <img src={product.image} alt={product.name} className="w-full h-full object-contain p-2 transition-transform group-hover:scale-105" />
            <button onClick={(e) => openLightbox(product.image, e)} className="absolute bottom-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg cursor-zoom-in transition-all md:opacity-0 md:group-hover:opacity-100" type="button">
              <ZoomIn className="w-3.5 h-3.5 text-gray-700" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-1.5">
            <h3 className="font-bold text-gray-900 uppercase leading-tight text-sm md:text-sm">{product.name}</h3>

            {product.label && (
              <p className="text-[11px] font-semibold text-stocker-green italic">{product.label}</p>
            )}

            {/* Coverage + Badge */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center bg-stocker-green/10 text-stocker-green px-2 py-0.5 rounded text-[11px] font-semibold">
                {product.coverage.toLocaleString()} m²
              </span>
              {badge && (
                <span data-testid={`coverage-badge-${product.id}`} className={`text-[11px] font-medium rounded px-1.5 py-0.5 ${badge.type === 'perfect' ? 'bg-green-50 text-green-700' : badge.type === 'reserve' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                  {badge.type === 'warning' && <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />}
                  {badge.text}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-500 text-[11px] leading-snug line-clamp-2">{product.description}</p>

            {/* Bullets */}
            <ul className="space-y-0.5">
              {product.bullets?.slice(0, 3).map((bullet, idx) => (
                <li key={`bullet-${product.id}-${idx}`} className="flex items-start gap-1.5 text-gray-600 text-[11px] leading-snug">
                  <Check className="w-3 h-3 text-stocker-green flex-shrink-0 mt-0.5" />
                  <span className="line-clamp-1">{bullet}</span>
                </li>
              ))}
            </ul>

            {/* Price */}
            <div className="pt-2 border-t border-gray-100">
              {product.price ? (
                <div className="flex items-baseline gap-2">
                  {product.originalPrice && (
                    <span className="text-[11px] text-gray-400 line-through">€ {product.originalPrice.toLocaleString()}</span>
                  )}
                  <span className="font-extrabold text-red-600 text-base md:text-base">
                    € {product.price.toLocaleString()}
                  </span>
                </div>
              ) : (
                <div className="text-xs font-bold text-gray-600 italic">Prezzo su richiesta</div>
              )}
            </div>
          </div>
        </div>

        {isSelected && (
          <div className="absolute top-3 right-3 w-6 h-6 bg-stocker-green rounded-full flex items-center justify-center shadow-md">
            <Check className="w-3.5 h-3.5 text-white" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto pt-2 md:pt-4 pb-28 md:pb-32">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-stocker-green mb-4 md:mb-6 transition-colors text-sm">
        <ChevronLeft className="w-4 h-4" />
        <span className="font-medium">{t.backToGarden}</span>
      </button>

      <div className="text-center mb-4 md:mb-8">
        <h1 className="text-lg md:text-3xl font-bold text-gray-900 mb-1 md:mb-3 uppercase tracking-wide">{t.selectKit}</h1>
        <p className="text-gray-600 text-xs md:text-base">
          {t.basedOnArea} (~{gardenSize.toLocaleString()} m²) {t.recommendIdealKit}
        </p>
      </div>

      {/* ====== MOBILE: Horizontal scroll snap ====== */}
      {isMobile && (
        <div className="relative mb-4">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-3 pt-4 px-4 -mx-4 no-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {sortedProducts.map((product, idx) => (
              <ProductCard key={product.id} product={product} index={idx} />
            ))}
          </div>
          
          {/* Mobile nav arrows */}
          <div className="flex justify-center items-center gap-4 mt-2">
            <button onClick={slideLeft} disabled={currentIndex === 0} className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30">
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-xs text-gray-500 font-medium">{currentIndex + 1} / {sortedProducts.length}</span>
            <button onClick={slideRight} disabled={currentIndex >= sortedProducts.length - 1} className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30">
              <ChevronRightIcon className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* ====== DESKTOP: Slider with translateX ====== */}
      {!isMobile && (
        <div className="relative mb-6">
          {currentIndex > 0 && (
            <button data-testid="slide-left" onClick={slideLeft} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 border border-gray-200">
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
          )}
          <div className="overflow-hidden px-2 pt-4">
            <div className="flex gap-4 transition-transform duration-300 ease-in-out" style={{ transform: `translateX(-${currentIndex * (100 / desktopVisible)}%)` }}>
              {sortedProducts.map((product, idx) => (
                <ProductCard key={product.id} product={product} index={idx} />
              ))}
            </div>
          </div>
          {currentIndex < maxDesktopIndex && (
            <button data-testid="slide-right" onClick={slideRight} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 border border-gray-200">
              <ChevronRightIcon className="w-6 h-6 text-gray-700" />
            </button>
          )}
        </div>
      )}

      {/* Offer validity */}
      <div className="text-center mb-4">
        <p className="text-xs md:text-sm text-red-600 font-medium">Offerta valida fino al 30.09.2026</p>
      </div>

      {/* Desktop dots */}
      {!isMobile && (
        <div className="flex justify-center gap-2 mb-6">
          {Array.from({ length: maxDesktopIndex + 1 }).map((_, idx) => (
            <button key={`dot-${idx}`} onClick={() => setCurrentIndex(idx)} className={`w-2 h-2 rounded-full transition-colors ${idx === currentIndex ? 'bg-stocker-green' : 'bg-gray-300'}`} />
          ))}
        </div>
      )}

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] px-3 md:px-4 py-2.5 md:py-3">
        <div className="max-w-6xl mx-auto">
          {selected && (
            <p className="text-center text-xs md:text-sm text-gray-600 mb-1.5 md:mb-2 truncate">
              <span className="font-bold text-gray-900">{selected.name}</span>
              <span className="text-stocker-green font-bold"> {selected.coverage.toLocaleString()} m²</span>
              {selected.price && <span className="font-bold text-red-600"> € {selected.price.toLocaleString()}</span>}
            </p>
          )}
          <div className="max-w-md mx-auto">
            <Button data-testid="continue-to-summary-btn" onClick={handleSubmit} disabled={!selected || isLoading} className="w-full min-h-[44px] md:min-h-[48px] bg-stocker-orange hover:bg-stocker-orange-hover text-white font-bold text-sm uppercase tracking-wide disabled:opacity-50">
              {isLoading ? 'Caricamento...' : 'TROVA RIVENDITA'}
            </Button>
          </div>
        </div>
      </div>

      {lightboxImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
          <button onClick={() => setLightboxImage(null)} className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center"><X className="w-6 h-6 text-white" /></button>
          <img src={lightboxImage} alt="Product detail" className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default Step2KitSelection;
